import { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DialerSipIcon from '@mui/icons-material/DialerSip';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Icon,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Apps,
  Menu as MenuIcon,
  Dashboard,
  Campaign,
  Code,
  PhoneInTalk,
  Assessment,
  CalendarMonth,
  Settings,
  ChevronLeft,
  People,
  Business,
  Hub,
  Person,
  Security,
  AccountBalanceWallet,
  Extension,
  Timelapse,
  Logout,
  Help,
  PhoneDisabled,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import useTenantAttendanceMetrics from '../hooks/useTenantAttendanceMetrics';
import { useFrontendVersionGuard } from '../hooks/useFrontendVersionGuard';
import useTenantPressureMetrics from '../hooks/useTenantPressureMetrics';
import { useSipExtension } from '../hooks/useSipExtension';
import { useOperatorLivePresence } from '../contexts/OperatorLivePresenceContext';
import { useCallWebSocket, type CallWebSocketEvent } from '../hooks/useCallWebSocket';
import { useSessionWebSocket } from '../hooks/useSessionWebSocket';
import useTenantUsageMetrics from '../hooks/useTenantUsageMetrics';
import AlertsDrawer from '../components/AlertsDrawer';
import OperatorAvailabilityPill from '../components/OperatorAvailabilityPill';
import TenantAttendanceIndicator from '../components/TenantAttendanceIndicator';
import TenantConcurrencyPressureIndicator from '../components/TenantConcurrencyPressureIndicator';
import TenantSwitcher from '../components/TenantSwitcher';
import TenantMinutesUsageIndicator from '../components/TenantMinutesUsageIndicator';
import CallDetailDrawer from '../components/CallDetailDrawer';
import RequiredUserActionModal from '../components/RequiredUserActionModal';
import { useCallDetailDrawer } from '../contexts/CallDetailDrawerContext';
import GlobalTelephonyFab, {
  type ManualCallLaunchPayload,
  type OutboundDialResult,
} from '../components/GlobalTelephonyFab';
import api from '../services/api';
import type { AuthBootstrapCapabilities, CallStatus, TenantPressureMetricsCampaignLoadRow } from '../types';
import {
  isOperatorReadinessBlockedByAvailability,
  isOperatorReadinessGuardEnabled,
} from '../utils/operatorTelephonyAvailability';

const drawerWidth = 260;
const DESKTOP_DRAWER_STORAGE_KEY = 'contacttel-desktop-drawer-open';
const TERMINAL_CALL_STATUSES: ReadonlySet<CallStatus> = new Set([
  'completed',
  'no_interaction',
  'failed',
  'no_answer',
  'no_answer_timeout',
  'busy',
  'cancelled',
  'transfer_unanswered',
  'congestion',
  'voicemail',
  'invalid_number',
  'config_error',
  'declined',
  'unreachable',
  'timeout',
  'mismatch',
  'api_timeout',
  'terminated_manual',
]);

const getDesktopDrawerInitialState = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedValue = window.localStorage.getItem(DESKTOP_DRAWER_STORAGE_KEY);
  if (storedValue === 'false') {
    return false;
  }
  if (storedValue === 'true') {
    return true;
  }
  return true;
};

const isDocumentDiscarded = (documentValue: Document): boolean => (
  'wasDiscarded' in documentValue
  && (documentValue as Document & { wasDiscarded?: boolean }).wasDiscarded === true
);

interface MenuItemType {
  textKey: string;
  tooltipKey?: string;
  icon: React.ReactNode;
  path: string;
  permission?: string | string[];
  capability?: keyof AuthBootstrapCapabilities;
}

const menuItemsConfig: MenuItemType[] = [
  { textKey: 'menu.dashboard', tooltipKey: 'menu.tooltip.dashboard', icon: <Dashboard />, path: '/' },
  { textKey: 'menu.campaigns', tooltipKey: 'menu.tooltip.campaigns', icon: <Campaign />, path: '/campaigns', permission: 'campaigns_access' },
  { textKey: 'menu.scripts', tooltipKey: 'menu.tooltip.scripts', icon: <Code />, path: '/scripts', permission: 'scripts_access' },
  { textKey: 'menu.calls', tooltipKey: 'menu.tooltip.calls', icon: <PhoneInTalk />, path: '/calls', permission: 'calls_access' },
  { textKey: 'menu.appointments', tooltipKey: 'menu.tooltip.appointments', icon: <CalendarMonth />, path: '/appointments', permission: 'appointments_access' },
  { textKey: 'menu.reports', tooltipKey: 'menu.tooltip.reports', icon: <Assessment />, path: '/reports', permission: ['campaigns_access', 'calls_reports_access', 'users_read'] },
  { textKey: 'menu.providers', tooltipKey: 'menu.tooltip.providers', icon: <Hub />, path: '/providers', permission: 'providers_access' },
  { textKey: 'menu.agents', tooltipKey: 'menu.tooltip.agents', icon: <PsychologyIcon />, path: '/agents', permission: 'agents_access' },
  { textKey: 'menu.aiAgentEndpoints', tooltipKey: 'menu.tooltip.aiAgentEndpoints', icon: <AltRouteIcon />, path: '/ai-agent-endpoints', permission: 'ai_agent_endpoints_access' },
  { textKey: 'menu.sipTrunks', tooltipKey: 'menu.tooltip.sipTrunks', icon: <DialerSipIcon />, path: '/sip-trunks', permission: 'sip_trunks_access' },
  { textKey: 'menu.integrations', tooltipKey: 'menu.tooltip.integrations', icon: <Apps />, path: '/integrations', permission: 'integrations_access' },
  { textKey: 'menu.users', tooltipKey: 'menu.tooltip.users', icon: <People />, path: '/users', permission: 'users_access' },
  { textKey: 'menu.groups', tooltipKey: 'menu.tooltip.groups', icon: <Security />, path: '/groups', permission: 'groups_access' },
  { textKey: 'menu.tenants', tooltipKey: 'menu.tooltip.tenants', icon: <Business />, path: '/tenants', permission: 'tenants_access' },
  { textKey: 'menu.subscriptionPlans', tooltipKey: 'menu.tooltip.subscriptionPlans', icon: <AccountBalanceWallet />, path: '/subscriptions/plans', permission: 'subscriptions_plans_access' },
  { textKey: 'menu.subscriptionAddons', tooltipKey: 'menu.tooltip.subscriptionAddons', icon: <Extension />, path: '/subscriptions/addons', permission: 'subscriptions_addons_access' },
  { textKey: 'menu.subscriptionPacks', tooltipKey: 'menu.tooltip.subscriptionPacks', icon: <Timelapse />, path: '/subscriptions/minute-packs', permission: 'subscriptions_packs_access' },
  { textKey: 'menu.transferGroups', tooltipKey: 'menu.tooltip.transferGroups', icon: <Hub />, path: '/transfer-groups', permission: 'transfer_groups_access' },
  { textKey: 'menu.settings', tooltipKey: 'menu.tooltip.settings', icon: <Settings />, path: '/settings', permission: 'settings_access' },
  {
    textKey: 'menu.help',
    tooltipKey: 'menu.tooltip.help',
    icon: <Help />,
    path: '/help',
    capability: 'hasPublicApiEnabled',
  },
];

export default function MainLayout() {
  useSessionWebSocket();
  const { t, i18n } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { subscriptions, user, logout, currentTenant, capabilities } = useAuth();
  const { hasPermission, isSuperuser } = usePermissions();
  const {
    activeCallId,
    isOpen: isCallDetailOpen,
    closeCallDetail,
    activeOptions,
    openCallDetail,
  } = useCallDetailDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    status: sipStatus,
    isEligible: sipIsEligible,
    connectionState: sipConnectionState,
    isRegistering: isSipRegistering,
    incomingCall: sipIncomingCall,
    reconcileTerminalCallState,
  } = useSipExtension();
  const {
    readinessState: livePresenceReadinessState,
    isConfirmingReadiness,
    confirmReadyForTransfers,
  } = useOperatorLivePresence();
  const [lastTelephonyCallId, setLastTelephonyCallId] = useState<string | null>(null);
  const [discardedNoticeOpen, setDiscardedNoticeOpen] = useState(false);
  const [operatorReadinessBannerError, setOperatorReadinessBannerError] = useState<string | null>(null);
  const hasSipCallInProgress = sipStatus === 'in_call' || sipStatus === 'calling';
  const isVersionReloadBlocked = hasSipCallInProgress || isSipRegistering || Boolean(sipIncomingCall);
  const versionGuard = useFrontendVersionGuard();
  const aiSubscription = subscriptions?.ai?.summary ?? null;
  const {
    aiSubscription: liveAiSubscription,
    reverseIvrSubscription: liveReverseIvrSubscription,
  } = useTenantUsageMetrics();
  const {
    ai: liveAiPressureMetrics,
    reverseIvr: liveReverseIvrPressureMetrics,
  } = useTenantPressureMetrics();
  const { telephony: telephonyRuntime } = useTenantAttendanceMetrics();
  const subscription = aiSubscription ?? null;
  const subscriptionOverageSeconds = Math.max(
    (subscription?.overageAiSeconds ?? 0) + (subscription?.overageTelephonySeconds ?? 0),
    0,
  );
  const subscriptionOverageMinutes = Math.ceil(subscriptionOverageSeconds / 60);
  const isViewingSwitchedTenant = Boolean(
    isSuperuser
      && user?.tenantId
      && currentTenant?.id
      && currentTenant.id !== user.tenantId,
  );
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [desktopOpen, setDesktopOpen] = useState(getDesktopDrawerInitialState);
  const [mobileOpen, setMobileOpen] = useState(false);
  const totalOccupiedConcurrency = useMemo(
    () => (
      Math.max(liveAiPressureMetrics?.concurrencyUsed ?? 0, 0)
      + Math.max(liveReverseIvrPressureMetrics?.concurrencyUsed ?? 0, 0)
    ),
    [liveAiPressureMetrics, liveReverseIvrPressureMetrics],
  );
  const totalContractedConcurrency = useMemo(
    () => (
      Math.max(liveAiPressureMetrics?.concurrencyLimit ?? 0, 0)
      + Math.max(liveReverseIvrPressureMetrics?.concurrencyLimit ?? 0, 0)
    ),
    [liveAiPressureMetrics, liveReverseIvrPressureMetrics],
  );
  const concurrencyCampaignLoadRows = useMemo<TenantPressureMetricsCampaignLoadRow[]>(
    () => (
      [
        ...(liveAiPressureMetrics?.campaignLoadRows ?? []).map((row) => ({
          ...row,
          campaignType: 'ai' as const,
        })),
        ...(liveReverseIvrPressureMetrics?.campaignLoadRows ?? []).map((row) => ({
          ...row,
          campaignType: 'reverse_ivr' as const,
        })),
      ].sort((left, right) => (
        (right.activePressure - left.activePressure)
        || String(left.campaignName || '').localeCompare(String(right.campaignName || ''))
        || String(left.campaignId || '').localeCompare(String(right.campaignId || ''))
      ))
    ),
    [liveAiPressureMetrics, liveReverseIvrPressureMetrics],
  );

  useEffect(() => {
    if (!versionGuard.updateAvailable || isVersionReloadBlocked || typeof window === 'undefined') {
      return;
    }

    const tryReload = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus()) {
        window.location.reload();
      }
    };

    tryReload();
    window.addEventListener('blur', tryReload);
    document.addEventListener('visibilitychange', tryReload);

    return () => {
      window.removeEventListener('blur', tryReload);
      document.removeEventListener('visibilitychange', tryReload);
    };
  }, [isVersionReloadBlocked, versionGuard.updateAvailable]);

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAccountClick = () => {
    handleMenuClose();
    navigate('/account');
  };

  useEffect(() => {
    if (isDesktop) {
      setMobileOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (typeof document === 'undefined' || !sipIsEligible || !isDocumentDiscarded(document)) {
      return;
    }
    setDiscardedNoticeOpen(true);
  }, [sipIsEligible]);

  useEffect(() => {
    if (!isDesktop || typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      DESKTOP_DRAWER_STORAGE_KEY,
      desktopOpen ? 'true' : 'false',
    );
  }, [desktopOpen, isDesktop]);

  const drawerToggleIcon = (isDesktop ? desktopOpen : mobileOpen) ? <ChevronLeft /> : <MenuIcon />;
  const drawerWidthValue = isDesktop ? (desktopOpen ? drawerWidth : 72) : drawerWidth;
  const appBarWidth = isDesktop ? `calc(100% - ${drawerWidthValue}px)` : '100%';
  const appBarMarginLeft = isDesktop ? `${drawerWidthValue}px` : 0;
  const listIsExpanded = isDesktop ? desktopOpen : true;
  const listButtonJustify = listIsExpanded ? 'initial' : 'center';
  const listButtonPaddingX = listIsExpanded ? 2.5 : 1;
  const currentFrontendVersion = versionGuard.current.version;
  const currentFrontendBuildCommit = versionGuard.current.buildCommit;
  const currentFrontendBuiltAt = versionGuard.current.builtAt;
  const sidebarVersion = useMemo(() => {
    const version = String(currentFrontendVersion || '').trim() || 'unknown';
    return version.startsWith('v') ? version : `v${version}`;
  }, [currentFrontendVersion]);
  const sidebarBuildCommit = useMemo(() => {
    const buildCommit = String(currentFrontendBuildCommit || '').trim();
    if (!buildCommit || buildCommit === 'unknown') {
      return null;
    }
    return buildCommit.slice(0, 7);
  }, [currentFrontendBuildCommit]);
  const sidebarBuiltAt = useMemo(() => {
    const builtAt = String(currentFrontendBuiltAt || '').trim();
    if (!builtAt) {
      return null;
    }

    const parsedDate = new Date(builtAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return builtAt;
    }

    const localeByLanguage: Record<string, string> = {
      en: 'en-US',
      'pt-BR': 'pt-BR',
      es: 'es-ES',
    };

    return new Intl.DateTimeFormat(localeByLanguage[i18n.language] || i18n.language || 'en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(parsedDate);
  }, [currentFrontendBuiltAt, i18n.language]);

  const discardedConnectionLabel = useMemo(() => {
    if (sipConnectionState === 'connected') {
      return t('common:operatorAvailability.connection.connected');
    }
    if (sipConnectionState === 'checking') {
      return t('common:operatorAvailability.connection.checking');
    }
    if (sipConnectionState === 'recovering') {
      return t('common:operatorAvailability.connection.recovering');
    }
    if (sipConnectionState === 'disconnected') {
      return t('common:operatorAvailability.connection.disconnected');
    }
    if (sipConnectionState === 'blocked_auth') {
      return t('common:operatorAvailability.connection.blockedAuth');
    }
    if (sipConnectionState === 'error') {
      return t('common:operatorAvailability.connection.error');
    }
    return t('common:operatorAvailability.discardedDialog.unknownState');
  }, [sipConnectionState, t]);

  // Filter menu items based on permissions and translate
  const visibleMenuItems = menuItemsConfig
    .filter(item => {
      if (item.capability && !capabilities?.[item.capability]) return false;
      if (!item.permission) return true;
      return hasPermission(item.permission);
    })
    .map(item => ({
      ...item,
      text: t(item.textKey),
      tooltip: t(item.tooltipKey ?? item.textKey),
    }));
  const canViewCostCurrency = hasPermission('view_metrics');
  const canViewCostCredits = hasPermission('view_metrics');
  const canCreateCalls = hasPermission('calls_create');
  const canManualLaunchAi = hasPermission('calls_manual_launch_ai');
  const canManualLaunchReverseIvr = hasPermission('calls_manual_launch_reverse_ivr');
  const canManualLaunchSip = hasPermission('calls_manual_launch_sip');
  const canOpenManualCallLauncher = canCreateCalls
    && (canManualLaunchAi || canManualLaunchReverseIvr || canManualLaunchSip);
  const operatorTelephonyAvailability = user?.telephonyAvailability ?? null;
  const operatorReadinessState = operatorTelephonyAvailability?.readinessState ?? livePresenceReadinessState;
  const operatorCanUseTelephonyControl = Boolean(
    operatorTelephonyAvailability?.isEligible
      && (operatorTelephonyAvailability?.isTenantEligible ?? true)
      && canCreateCalls
      && canManualLaunchSip,
  );
  const operatorReadinessNeedsConfirmation = isOperatorReadinessBlockedByAvailability(
    operatorTelephonyAvailability,
    livePresenceReadinessState,
  );
  const operatorReadinessGuardEnabled = isOperatorReadinessGuardEnabled(
    operatorTelephonyAvailability,
  );
  const operatorSipConnectionNeedsAttention = (
    sipConnectionState === 'disconnected'
    || sipConnectionState === 'blocked_auth'
    || sipConnectionState === 'error'
  );
  const showOperatorReadinessBanner = Boolean(
    operatorCanUseTelephonyControl
      && operatorReadinessGuardEnabled
      && operatorTelephonyAvailability?.status === 'available'
      && (operatorReadinessNeedsConfirmation || operatorSipConnectionNeedsAttention),
  );
  const operatorReadinessBannerStateLabel = useMemo(() => {
    if (sipConnectionState === 'disconnected') {
      return t('common:operatorAvailability.connection.disconnected');
    }
    if (sipConnectionState === 'blocked_auth') {
      return t('common:operatorAvailability.connection.blockedAuth');
    }
    if (sipConnectionState === 'error') {
      return t('common:operatorAvailability.connection.error');
    }
    if (operatorReadinessState === 'idle') {
      return t('common:operatorAvailability.states.operatorIdle');
    }
    if (operatorReadinessState === 'hidden') {
      return t('common:operatorAvailability.states.operatorHidden');
    }
    if (operatorReadinessState === 'needs_confirmation') {
      return t('common:operatorAvailability.states.operatorNeedsConfirmation');
    }
    return t('common:operatorAvailability.discardedDialog.unknownState');
  }, [operatorReadinessState, sipConnectionState, t]);
  const outboundEndpointLabel = useMemo(() => {
    const endpoint = user?.telephonyEndpoint;
    if (!endpoint?.isActive || !endpoint.extension?.trim()) {
      return null;
    }
    if (endpoint.endpointType === 'internal_extension') {
      return t('common:telephonyFab.endpoint.internalExtension', { extension: endpoint.extension });
    }
    if (endpoint.endpointType === 'external_extension') {
      return t('common:telephonyFab.endpoint.externalExtension', { extension: endpoint.extension });
    }
    return endpoint.extension;
  }, [t, user?.telephonyEndpoint]);

  const openCallDrawerOnCallsPage = useCallback((callId: string, options: {
    initialTab: 'information' | 'general' | 'variables' | 'dataCollected' | 'scripts' | 'diagnostics';
    controlMode: 'default' | 'incoming' | 'outbound_self';
  }) => {
    openCallDetail(callId, options);
    const nextSearch = new URLSearchParams();
    nextSearch.set('call', callId);
    navigate({
      pathname: '/calls',
      search: `?${nextSearch.toString()}`,
    });
  }, [navigate, openCallDetail]);

  const handleIncomingFabClick = useCallback(() => {
    const incomingCallPublicId = String(sipIncomingCall?.callPublicId || '').trim();
    if (incomingCallPublicId) {
      setLastTelephonyCallId(incomingCallPublicId);
      openCallDrawerOnCallsPage(incomingCallPublicId, {
        initialTab: 'general',
        controlMode: 'incoming',
      });
      return;
    }
    if (lastTelephonyCallId) {
      openCallDrawerOnCallsPage(lastTelephonyCallId, {
        initialTab: 'general',
        controlMode: 'incoming',
      });
      return;
    }
    navigate('/calls');
  }, [lastTelephonyCallId, navigate, openCallDrawerOnCallsPage, sipIncomingCall?.callPublicId]);

  const handleInProgressFabClick = useCallback(() => {
    const callId = String(sipIncomingCall?.callPublicId || '').trim() || String(lastTelephonyCallId || '').trim();
    if (!callId) {
      navigate('/calls');
      return;
    }
    openCallDrawerOnCallsPage(callId, {
      initialTab: 'general',
      controlMode: 'incoming',
    });
  }, [lastTelephonyCallId, navigate, openCallDrawerOnCallsPage, sipIncomingCall?.callPublicId]);

  const loadCallBootstrap = useCallback(async () => {
    const response = await api.get('/api/calls/bootstrap/');
    return (response?.data ?? {}) as Record<string, unknown>;
  }, []);

  const handleManualLaunch = useCallback(async (payload: ManualCallLaunchPayload): Promise<OutboundDialResult> => {
    const response = await api.post('/api/calls/manual_call_launch/', payload);
    const responseData = response?.data as
      | { id?: string; callId?: string; call?: { id?: string } }
      | undefined;
    const callId = responseData?.id || responseData?.callId || responseData?.call?.id || null;
    if (callId) {
      setLastTelephonyCallId(String(callId));
      openCallDrawerOnCallsPage(String(callId), {
        initialTab: 'general',
        controlMode: 'outbound_self',
      });
    }
    return { callId };
  }, [openCallDrawerOnCallsPage]);

  const handleConfirmOperatorReadiness = useCallback(async () => {
    setOperatorReadinessBannerError(null);
    try {
      await confirmReadyForTransfers();
    } catch (requestError) {
      console.error('Error confirming operator readiness from banner:', requestError);
      setOperatorReadinessBannerError(t('common:operatorAvailability.banner.error'));
    }
  }, [confirmReadyForTransfers, t]);

  useEffect(() => {
    if (!showOperatorReadinessBanner) {
      setOperatorReadinessBannerError(null);
    }
  }, [showOperatorReadinessBanner]);

  useEffect(() => {
    const incomingCallPublicId = String(sipIncomingCall?.callPublicId || '').trim();
    if (!incomingCallPublicId) {
      return;
    }
    setLastTelephonyCallId(incomingCallPublicId);
  }, [sipIncomingCall?.callPublicId]);

  const handleTrackedCallWebSocketEvent = useCallback((event: CallWebSocketEvent) => {
    const trackedCallId = String(lastTelephonyCallId || '').trim();
    const eventCallId = String(event.id || '').trim();
    if (!trackedCallId || !eventCallId || eventCallId !== trackedCallId) {
      return;
    }

    const eventStatus = String(event.status || '').trim() as CallStatus;
    const isTerminal = Boolean(event.endedAt) || TERMINAL_CALL_STATUSES.has(eventStatus);
    if (!isTerminal) {
      return;
    }

    reconcileTerminalCallState(eventCallId, { force: true });
  }, [lastTelephonyCallId, reconcileTerminalCallState]);

  useCallWebSocket(lastTelephonyCallId, {
    enabled: Boolean(lastTelephonyCallId) && hasSipCallInProgress,
    onEvent: handleTrackedCallWebSocketEvent,
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: appBarWidth,
          ml: appBarMarginLeft,
          transition: 'width 0.2s, margin 0.2s',
          color: isDarkMode ? '#fff' : '#111',
        }}
        elevation={0}
      >
        <Toolbar sx={{ paddingX: 3 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
        {drawerToggleIcon}
          </IconButton>
          <Box
            component="img"
            src={isDarkMode ? '/contacttel-logo-dark.png' : '/contacttel-logo-light.png'}
            alt="Contact Tel"
            sx={{
              height: 32,
              width: 'auto',
              mr: 2,
            }}
          />
          
          <Box sx={{ flexGrow: 1}} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* Tenant Switcher - only for superusers */}
            {isDesktop && <TenantSwitcher />}
            {capabilities?.canViewPressureMetrics ? (
              <TenantConcurrencyPressureIndicator
                occupiedConcurrency={totalOccupiedConcurrency}
                totalConcurrency={totalContractedConcurrency}
                campaignLoadRows={concurrencyCampaignLoadRows}
              />
            ) : null}
            {capabilities?.canViewAttendanceMetrics && telephonyRuntime ? (
              <TenantAttendanceIndicator
                telephony={telephonyRuntime}
              />
            ) : null}
            {capabilities?.canViewUsageMetrics ? (
              <TenantMinutesUsageIndicator
                aiSubscription={liveAiSubscription}
                telephonySubscription={liveAiSubscription ?? liveReverseIvrSubscription}
              />
            ) : null}
            <OperatorAvailabilityPill />
            <AlertsDrawer />

            <IconButton onClick={handleMenuOpen} sx={{ p: 0, position: 'relative', m: 0 }} size="small">
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 16 }}>
                {user?.firstName?.[0] && user?.lastName?.[0] ? `${user.firstName[0]}${user.lastName[0]}` : user?.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>

              {isSuperuser && (
              <Icon color="warning" sx={{ fontSize: 24, position: 'absolute', top: -8, right: -14 }}>
                <LocalPoliceIcon />
              </Icon>
            )}
            </IconButton>
          </Box>

          
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={600}>{user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                {isSuperuser && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-start', flexDirection: 'row' }}>
                    <Icon color="warning" sx={{ m: 0, p: 0, height: 'auto', width: 'auto' }}>
                      <LocalPoliceIcon sx={{ fontSize: 16, m: 0, p: 0 }} />
                    </Icon>
                    <Typography variant="caption" color="text.secondary">
                      {t('user.superuser')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </MenuItem>
            <Divider />
            {!isDesktop && (
              <MenuItem
                disableRipple
                disableTouchRipple
                sx={{ cursor: 'default', py: 0 }}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <TenantSwitcher />
              </MenuItem>
            )}
            <MenuItem onClick={handleAccountClick}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              {t('user.myAccount')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                void logout();
              }}
            >
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              {t('auth.signOut')}
            </MenuItem>
          </Menu>
        </Toolbar>
        {subscription && subscription.status !== 'active' && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              width: '100%',
              borderRadius: 0,
              px: 3,
              fontFamily: '"Barlow Condensed", sans-serif',
              backgroundColor: 'rgba(255,45,109,0.1)',
              color: isDarkMode ? '#F9F5FF' : '#1A1A1A',
              borderColor: 'rgba(255,45,109,0.5)',
            }}
          >
            {t('common:subscription.banner', {
              status: t(`common:subscription.status.${subscription.status}`),
            })}
          </Alert>
        )}
        {subscription && subscriptionOverageSeconds > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              width: '100%',
              borderRadius: 0,
              px: 3,
              fontFamily: '"Barlow Condensed", sans-serif',
              backgroundColor: 'rgba(255,45,109,0.1)',
              color: isDarkMode ? '#F9F5FF' : '#1A1A1A',
              borderColor: 'rgba(255,45,109,0.5)',
            }}
          >
            {t('common:subscription.overageBanner', {
              minutes: subscriptionOverageMinutes,
            })}
          </Alert>
        )}
      </AppBar>

      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidthValue,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidthValue,
            boxSizing: 'border-box',
            transition: 'width 0.2s',
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'flex-end', px: listIsExpanded ? 2.5 : 1.5, display: { xs: 'flex', sm: 'none' } }}>
          <IconButton onClick={handleDrawerToggle} size="small">
            {drawerToggleIcon}
          </IconButton>
        </Toolbar>
        
        <Divider sx={{display: { xs: 'flex', sm: 'none' }}} />
        
          <List
            sx={{
              px: 1,
              pt: { xs: 3, sm: 1 },
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
            }}
          >
          {visibleMenuItems.map((item) => {
            const isItemSelected =
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={item.tooltip} arrow>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (!isDesktop) {
                        setMobileOpen(false);
                      }
                    }}
                    selected={isItemSelected}
                    sx={{
                      borderRadius: 2,
                      minHeight: 48,
                      justifyContent: listButtonJustify,
                      px: listButtonPaddingX,
                      transition: 'background 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                    }}
                  >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: listIsExpanded ? 2 : 'none',
                      justifyContent: 'center',
                      color: isItemSelected ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {listIsExpanded && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isItemSelected ? 600 : 400,
                        color: isItemSelected ? 'primary.main' : 'text.primary',
                      }}
                    />
                  )}
                </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            <Divider sx={{ mb: 1.25 }} />
            <ListItem disablePadding>
              <Box
                sx={{
                  width: '100%',
                  px: listIsExpanded ? 2.5 : 1,
                  py: 1,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: listIsExpanded ? 'flex-start' : 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  opacity: 0.72,
                }}
              >
                {listIsExpanded ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.1,
                      }}
                    >
                      {t('versionGuard.sidebarLabel')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.15,
                        fontFamily: '"Roboto Mono", "Fira Code", monospace',
                        color: 'text.secondary',
                      }}
                    >
                      {sidebarVersion}
                    </Typography>
                    {sidebarBuildCommit && (
                      <Typography
                        variant="caption"
                        sx={{
                          lineHeight: 1.1,
                          fontFamily: '"Roboto Mono", "Fira Code", monospace',
                          color: 'text.disabled',
                        }}
                      >
                        {t('versionGuard.sidebarBuild', { buildCommit: sidebarBuildCommit })}
                      </Typography>
                    )}
                    {sidebarBuiltAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          lineHeight: 1.1,
                          color: 'text.disabled',
                        }}
                      >
                        {t('versionGuard.sidebarBuiltAt', { builtAt: sidebarBuiltAt })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      lineHeight: 1.1,
                      fontFamily: '"Roboto Mono", "Fira Code", monospace',
                      color: 'text.disabled',
                    }}
                  >
                    {sidebarVersion}
                  </Typography>
                )}
              </Box>
            </ListItem>
          </Box>
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          minHeight: '100vh',
          transition: 'width 0.2s',
          maxWidth: isDesktop && desktopOpen ? 'calc(100vw - 260px)' : '100vw',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 42, sm: 64 } }} />
        {showOperatorReadinessBanner && (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<PhoneDisabled fontSize="inherit" />}
            action={(
              <Button
                color="warning"
                variant="contained"
                size="small"
                disabled={isConfirmingReadiness}
                onClick={() => void handleConfirmOperatorReadiness()}
                sx={{
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {isConfirmingReadiness
                  ? t('common:operatorAvailability.banner.confirming')
                  : t('common:operatorAvailability.banner.action')}
              </Button>
            )}
            sx={{
              mb: 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.25, sm: 1.35 },
              borderRadius: 2,
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              bgcolor: alpha(theme.palette.warning.main, isDarkMode ? 0.16 : 0.1),
              borderColor: alpha(theme.palette.warning.main, isDarkMode ? 0.55 : 0.45),
              color: 'text.primary',
              fontFamily: '"Barlow Condensed", sans-serif',
              '& .MuiAlert-icon': {
                color: 'warning.main',
                mr: { xs: 1, sm: 1.5 },
              },
              '& .MuiAlert-message': {
                flex: 1,
                minWidth: 0,
                py: 0,
              },
              '& .MuiAlert-action': {
                width: { xs: '100%', sm: 'auto' },
                ml: { xs: 0, sm: 2 },
                mr: 0,
                pl: 0,
                pt: { xs: 1.1, sm: 0 },
                alignItems: 'center',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  fontSize: { xs: '1rem', sm: '1.05rem' },
                }}
              >
                {t('common:operatorAvailability.banner.title')}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                {t('common:operatorAvailability.banner.description')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.25 }}>
                {t('common:operatorAvailability.banner.currentState', {
                  state: operatorReadinessBannerStateLabel,
                })}
              </Typography>
              {operatorReadinessBannerError ? (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700, lineHeight: 1.2 }}>
                  {operatorReadinessBannerError}
                </Typography>
              ) : null}
            </Box>
          </Alert>
        )}
        {isViewingSwitchedTenant && (
          <Alert
            severity="warning"
            variant="filled"
            sx={{
              mb: 2,
              fontSize: '1.2rem',
              fontFamily: '"Barlow Condensed", sans-serif',
            }}
          >
            {t('tenantSwitcher.switchedWarning', {
              tenantName: currentTenant?.name ?? '',
            })}
          </Alert>
        )}
        {versionGuard.updateAvailable && (
          <Alert
            severity={isVersionReloadBlocked ? 'warning' : 'info'}
            variant="filled"
            sx={{
              mb: 2,
              fontSize: '1.1rem',
              fontFamily: '"Barlow Condensed", sans-serif',
            }}
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => window.location.reload()}
                sx={{ fontWeight: 700 }}
              >
                {t('versionGuard.reloadNow')}
              </Button>
            )}
          >
            {isVersionReloadBlocked
              ? t('versionGuard.blockedMessage', {
                  currentVersion: versionGuard.current.version,
                  latestVersion: versionGuard.latest?.version || versionGuard.current.version,
                })
              : t('versionGuard.message', {
                  currentVersion: versionGuard.current.version,
                  latestVersion: versionGuard.latest?.version || versionGuard.current.version,
                })}
          </Alert>
        )}
        <Outlet />
      </Box>
      <CallDetailDrawer
        open={isCallDetailOpen}
        callId={activeCallId}
        initialCall={activeOptions?.initialCall ?? null}
        initialTab={activeOptions?.initialTab ?? 'information'}
        controlMode={activeOptions?.controlMode ?? 'default'}
        onClose={closeCallDetail}
        canViewCostCurrency={canViewCostCurrency}
        canViewCostCredits={canViewCostCredits}
        contactVariables={activeOptions?.contactVariables}
      />
      <GlobalTelephonyFab
        visible={canOpenManualCallLauncher}
        hasIncomingAlert={Boolean(sipIncomingCall)}
        hasInProgressCall={hasSipCallInProgress}
        sipRegistering={isSipRegistering}
        outboundEndpointLabel={outboundEndpointLabel}
        loadCallBootstrap={loadCallBootstrap}
        onIncomingClick={handleIncomingFabClick}
        onInProgressClick={handleInProgressFabClick}
        onManualLaunch={handleManualLaunch}
      />
      <RequiredUserActionModal />
      <Dialog
        open={discardedNoticeOpen}
        onClose={() => setDiscardedNoticeOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t('common:operatorAvailability.discardedDialog.title')}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            {t('common:operatorAvailability.discardedDialog.description')}
          </Typography>
          <Alert severity="warning" variant="outlined">
            {t('common:operatorAvailability.discardedDialog.chromeHint')}
          </Alert>
          <Alert severity="info" variant="outlined">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('common:operatorAvailability.discardedDialog.recoveryTitle')}
            </Typography>
            <Typography variant="body2">
              {t('common:operatorAvailability.discardedDialog.recoveryBody')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
              {t('common:operatorAvailability.discardedDialog.currentState', {
                state: discardedConnectionLabel,
              })}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDiscardedNoticeOpen(false)} variant="contained">
            {t('common:operatorAvailability.discardedDialog.acknowledge')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
